<template>
    <div class="flex flex-col gap-6 pb-8 max-w-4xl mx-auto">
        <div class="text-center pt-2">
            <h1 class="text-3xl text-theme-primary">Creación rápida</h1>
            <p class="text-sm mt-1">
                Registra un producto con lo esencial. Podrás completarlo después
                desde el detalle del producto.
            </p>
        </div>

        <div class="rounded-xl px-8 py-7 flex flex-col gap-6 shadow-lg">
            <div class="flex flex-col md:flex-row gap-5 items-start">
                <div class="flex-1 w-full">
                    <custom-input
                        v-model="name"
                        label="Nombre del producto"
                        class="w-full"
                        :error="
                            !isValidName && touched.name
                                ? 'El nombre es requerido'
                                : ''
                        "
                        @blur="touched.name = true"
                    />
                </div>
                <div class="w-full md:w-64">
                    <barcode-scanner-input v-model="sku" enabled check />
                </div>
            </div>

            <input-money-component
                v-model="price"
                enabled
                label="Precio de venta"
                :message-error="
                    !isValidPrice && touched.price
                        ? 'El precio de venta debe ser mayor a $0'
                        : ''
                "
                @blur="touched.price = true"
            />

            <div>
                <p class="text-xs mb-2">Fotos del producto (opcional)</p>
                <images-manage
                    id="fast-product-images"
                    v-model="images"
                    message="del producto"
                    default-size="squareImage"
                    :default-alt="'Imagen de ' + (name || 'producto')"
                    :default-filters="{ size: 'squareImage' }"
                    enabled
                />
            </div>
        </div>

        <div class="mt-3 mb-5 flex flex-col md:flex-row justify-center gap-3">
            <app-button
                class="w-full md:w-auto"
                color="success"
                border
                :disabled="!canSubmit"
                :loading="isLoading"
                @click="submit(false)"
            >
                <span class="text-2xl">Crear producto</span>
            </app-button>
            <app-button
                class="w-full md:w-auto"
                color="success"
                :disabled="!canSubmit"
                :loading="isLoading"
                @click="submit(true)"
            >
                <span class="text-2xl">Crear y publicar</span>
            </app-button>
        </div>
    </div>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import { mapActions, mapGetters, mapState } from "vuex";

import { titleRegex } from "@/common/lib/regex";

import BarcodeScannerInput from "@/components/utils/BarcodeScannerInput.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImagesManage from "@/components/utils/ImagesManage.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";

export default {
    name: "ProductFastCreate",
    components: {
        BarcodeScannerInput,
        ImagesManage,
        InputMoneyComponent,
        CustomInput
    },
    mixins: [planLimitError],
    layout: "app",
    data: () => ({
        name: "",
        price: 1000,
        sku: "",
        images: [],
        isLoading: false,
        touched: { name: false, price: false }
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapGetters("control", ["backgroundColor"]),
        isValidName() {
            return titleRegex.test(this.name);
        },
        isValidPrice() {
            return this.price > 0;
        },
        canSubmit() {
            return this.isValidName && this.isValidPrice;
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        await verifyPagePermission("product:Record", "/app/products");
    },
    beforeDestroy() {
        this.$swal.close();
    },
    methods: {
        ...mapActions("products", ["recordProduct"]),
        async submit(active = false) {
            this.touched = { name: true, price: true };
            if (!this.canSubmit) return;

            this.isLoading = true;
            this.$loading.show();

            try {
                const payload = {
                    name: this.name,
                    price: this.price,
                    active,
                    sku: this.sku || undefined,
                    images: this.images.length
                        ? this.images.map((img) => ({
                              id: img.id,
                              sequence: img.sequence.sequence
                          }))
                        : undefined
                };

                if (this.selectedMerchant?.merchantId) {
                    payload.merchantId = this.selectedMerchant.merchantId;
                    payload.merchantType = this.selectedMerchant.merchantType;
                }

                const result = await this.recordProduct(payload);

                this.$analytics.trackEvent("product_created", {
                    creation_mode: "fast",
                    has_sku: !!this.sku,
                    has_images: this.images.length > 0,
                    has_merchant: !!this.selectedMerchant?.merchantId
                });
                this.$loading.hide();
                this.$sound.play();

                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Producto creado",
                    text: "¿Qué deseas hacer ahora?",
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista",
                    cancelButtonText: "Nuevo producto",
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#5C5CFF",
                    allowOutsideClick: false
                });

                if (dialogResult.isConfirmed) {
                    this.$router.push(`/app/products/detail/${result.id}`);
                } else if (dialogResult.isDenied) {
                    this.$router.push("/app/products/");
                } else {
                    this.name = "";
                    this.price = 1000;
                    this.sku = "";
                    this.images = [];
                    this.touched = { name: false, price: false };
                }
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                if (this.openPlanUpgradeForLimit(error)) return;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
